import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Users,
  TrendingUp,
  Target,
  CheckCircle,
  ArrowRight,
  Zap,
  BarChart3,
  Handshake,
  Building2,
  DollarSign,
  Layers,
  UserCheck,
  PieChart,
  Activity,
  X,
  ChevronDown,
  Lock,
} from 'lucide-react';
import AltHeader from '../components/AltHeader';
import AltFooter from '../components/AltFooter';
import AltParallaxBackdrop from '../components/AltParallaxBackdrop';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* Country codes (re-used from /download-app form). */
const countryCodes = [
  { code: '+54', country: 'Argentina' }, { code: '+61', country: 'Australia' },
  { code: '+43', country: 'Austria' }, { code: '+32', country: 'Belgium' },
  { code: '+55', country: 'Brazil' }, { code: '+1', country: 'Canada' },
  { code: '+86', country: 'China' }, { code: '+420', country: 'Czech Republic' },
  { code: '+45', country: 'Denmark' }, { code: '+358', country: 'Finland' },
  { code: '+33', country: 'France' }, { code: '+49', country: 'Germany' },
  { code: '+30', country: 'Greece' }, { code: '+852', country: 'Hong Kong' },
  { code: '+36', country: 'Hungary' }, { code: '+91', country: 'India' },
  { code: '+353', country: 'Ireland' }, { code: '+972', country: 'Israel' },
  { code: '+39', country: 'Italy' }, { code: '+81', country: 'Japan' },
  { code: '+52', country: 'Mexico' }, { code: '+31', country: 'Netherlands' },
  { code: '+64', country: 'New Zealand' }, { code: '+47', country: 'Norway' },
  { code: '+48', country: 'Poland' }, { code: '+351', country: 'Portugal' },
  { code: '+40', country: 'Romania' }, { code: '+7', country: 'Russia' },
  { code: '+966', country: 'Saudi Arabia' }, { code: '+65', country: 'Singapore' },
  { code: '+27', country: 'South Africa' }, { code: '+82', country: 'South Korea' },
  { code: '+34', country: 'Spain' }, { code: '+46', country: 'Sweden' },
  { code: '+41', country: 'Switzerland' }, { code: '+90', country: 'Turkey' },
  { code: '+380', country: 'Ukraine' }, { code: '+971', country: 'United Arab Emirates' },
  { code: '+44', country: 'United Kingdom' }, { code: '+1', country: 'United States' },
];

const ORG_BENEFITS = [
  { icon: Zap, title: 'Faster Activation', desc: 'New distributors get moving faster with clear first steps' },
  { icon: UserCheck, title: 'Better Onboarding', desc: 'Structured entry process reduces early confusion' },
  { icon: Users, title: 'Easier Duplication', desc: 'Consistent system makes teaching and replication simpler' },
  { icon: BarChart3, title: 'Higher Retention', desc: 'Ongoing development keeps distributors engaged longer' },
  { icon: Target, title: 'Consistent Activity', desc: 'Daily structure creates regular action across the team' },
  { icon: Building2, title: 'Less Pressure on Leaders', desc: 'System handles development, freeing leadership time' },
];

const ANALYTICS_POINTS = [
  { icon: Users, text: 'How many distributors started each training' },
  { icon: CheckCircle, text: 'How many completed it' },
  { icon: Activity, text: 'How many took the required actions' },
  { icon: BarChart3, text: 'Which trainings have low engagement' },
];

const DIFFERENTIATORS = [
  'Step-by-step progress',
  'Guided daily action',
  'Real practical tasks',
  'Measurable development',
];

const SUMMARY_BENEFITS = [
  'Better distributor activation',
  'Structured development path',
  'Personal growth support for distributors',
  'Practical MLM business guidance',
  'No upfront platform cost',
  'Revenue share opportunity',
];

const PartnersPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '', role: '', company_name: '',
    email: '', countryCode: '+1', phoneNumber: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const submitData = {
      full_name: formData.full_name,
      role: formData.role,
      company_name: formData.company_name,
      email: formData.email,
      phone: `${formData.countryCode} ${formData.phoneNumber}`,
    };

    try {
      const response = await fetch(`${API_URL}/api/partnership`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submitData),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Something went wrong');
      setSuccess(true);
      setFormData({ full_name: '', role: '', company_name: '', email: '', countryCode: '+1', phoneNumber: '' });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSuccess(false);
    setError('');
  };

  return (
    <div className="bg-white overflow-x-hidden">
      <AltHeader />

      {/* HERO */}
      <section className="relative pt-[110px] md:pt-[170px] pb-14 md:pb-24 px-6 overflow-hidden min-h-[580px] md:min-h-[760px]">
        <AltParallaxBackdrop />
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <span
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#0b1f3a]/80 backdrop-blur-md text-xs md:text-[13px] font-semibold tracking-[0.18em] uppercase text-white shadow-lg ring-1 ring-white/20 mb-7"
            data-testid="partners-eyebrow"
          >
            <Handshake className="w-3.5 h-3.5 text-cyan-300" strokeWidth={2.5} />
            Partnership Program
          </span>
          <h1 className="text-4xl md:text-7xl font-black leading-[1.02] tracking-tight text-[#0b1f3a] alt-hero-glow">
            Partner with{' '}
            <em className="italic font-light text-blue-600">Levanta.</em>
          </h1>
          <p className="mt-8 text-lg md:text-xl text-[#0b1f3a]/75 max-w-2xl mx-auto leading-relaxed alt-hero-glow">
            A structured development platform for network marketing companies looking
            to improve distributor activation, retention, and long-term performance.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <button
              onClick={() => setIsModalOpen(true)}
              data-testid="partners-hero-cta"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-[#0b1f3a] text-white font-semibold shadow-md hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-200"
            >
              Talk to us
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* THE PROBLEM */}
      <section className="px-6 py-24 md:py-28 bg-white">
        <div className="max-w-3xl mx-auto">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-rose-500 mb-5 text-center">
            The real challenge
          </p>
          <h2 className="text-3xl md:text-5xl font-black leading-[1.08] tracking-tight text-[#0b1f3a] text-center mb-12">
            Most companies don&rsquo;t have a product problem. They have an{' '}
            <em className="italic font-light text-rose-500">activation problem.</em>
          </h2>
          <div className="space-y-6 text-lg md:text-xl text-[#0b1f3a]/80 leading-relaxed">
            <p>
              New distributors join with enthusiasm, but quickly become confused,
              overwhelmed, and inactive. They often do not know what to do next.
              They stay in learning mode without taking real action. They fail to
              become consistently active.
            </p>
            <p>
              This creates a cycle of low activation, weak duplication, poor
              retention, and constant drop-off across the organization.
            </p>
            <p className="text-2xl md:text-3xl font-bold text-[#0b1f3a] pt-4">
              The issue is not lack of motivation. It&rsquo;s lack of{' '}
              <em className="italic font-light text-blue-600">structure.</em>
            </p>
          </div>
        </div>
      </section>

      {/* WHAT LEVANTA IS */}
      <section className="px-6 py-24 md:py-28 bg-[#fafbfd]">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 justify-center mb-5">
            <Layers className="w-4 h-4 text-blue-600" />
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-600">
              What Levanta is
            </p>
          </div>
          <h2 className="text-3xl md:text-5xl font-black leading-[1.08] tracking-tight text-[#0b1f3a] text-center mb-12">
            Personal growth and business growth.{' '}
            <em className="italic font-light text-blue-600">Same platform.</em>
          </h2>
          <div className="space-y-6 text-lg md:text-xl text-[#0b1f3a]/80 leading-relaxed">
            <p>
              Levanta is not just an MLM training platform. It is a{' '}
              <strong className="text-[#0b1f3a]">personal life growth system</strong>{' '}
              that helps people develop as individuals through structured skill
              building and daily action.
            </p>
            <p>
              At the same time, for partner companies, Levanta includes a dedicated{' '}
              <strong className="text-[#0b1f3a]">Partner System</strong> that helps
              distributors build their network marketing business in a more organized,
              action-based way.
            </p>
            <p>
              Levanta combines personal development and business development into
              one structured platform.
            </p>
          </div>
        </div>
      </section>

      {/* DUAL VALUE — what distributors receive */}
      <section className="px-6 py-24 md:py-28 bg-white">
        <div className="max-w-5xl mx-auto">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-600 mb-5 text-center">
            What distributors receive
          </p>
          <h2 className="text-3xl md:text-5xl font-black leading-[1.08] tracking-tight text-[#0b1f3a] text-center mb-12">
            Two systems.{' '}
            <em className="italic font-light text-blue-600">One subscription.</em>
          </h2>

          <div className="grid md:grid-cols-2 gap-5">
            <div className="rounded-3xl bg-white ring-1 ring-black/5 shadow-sm p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
              <p className="text-[11px] font-mono text-blue-600 font-semibold mb-3">01 / Personal</p>
              <h3 className="text-2xl md:text-3xl font-black text-[#0b1f3a] leading-tight mb-3">
                Levanta Personal Growth System
              </h3>
              <p className="text-[#0b1f3a]/75 leading-relaxed">
                Structured development across mindset, discipline, communication,
                confidence, and other essential life skills. Daily progress, clear
                milestones, and real personal improvement.
              </p>
            </div>
            <div className="rounded-3xl bg-white ring-1 ring-black/5 shadow-sm p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
              <p className="text-[11px] font-mono text-blue-600 font-semibold mb-3">02 / Business</p>
              <h3 className="text-2xl md:text-3xl font-black text-[#0b1f3a] leading-tight mb-3">
                Levanta Partner System
              </h3>
              <p className="text-[#0b1f3a]/75 leading-relaxed">
                A dedicated system for building and growing in network marketing.
                Step-by-step guidance, practical tasks, and structured action to
                support business development.
              </p>
            </div>
          </div>

          <div className="mt-10 rounded-2xl bg-gradient-to-br from-[#eaf3fb] to-[#d4e9f8] ring-1 ring-blue-200/40 p-6 md:p-7">
            <p className="text-[#0b1f3a]/85 text-base md:text-lg leading-relaxed">
              <strong className="text-[#0b1f3a]">Why this matters:</strong> Stronger
              people become better distributors. Personal growth directly supports
              business growth. Levanta helps distributors not only with business
              activity, but also with the mindset, discipline, consistency, and
              communication skills needed to succeed long-term.
            </p>
          </div>
        </div>
      </section>

      {/* HOW LEVANTA HELPS — 6 benefit grid */}
      <section className="px-6 py-24 md:py-28 bg-[#fafbfd]">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-3 justify-center mb-5">
            <TrendingUp className="w-4 h-4 text-blue-600" />
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-600">
              How Levanta helps your organization
            </p>
          </div>
          <h2 className="text-3xl md:text-5xl font-black leading-[1.08] tracking-tight text-[#0b1f3a] text-center mb-8">
            From random learning to a{' '}
            <em className="italic font-light text-blue-600">clear path.</em>
          </h2>
          <p className="text-lg text-[#0b1f3a]/75 text-center max-w-3xl mx-auto leading-relaxed mb-14">
            Instead of confusion and inactivity, distributors are guided through a
            clear step-by-step system with practical skills and real tasks. The
            emphasis is on action, progress, and structure.
          </p>

          {/* Desktop: 3-col grid (untouched). Mobile: horizontal swipe
              carousel — same pattern used on the homepage Worlds + Mission
              foundations. Saves vertical scroll while preserving every
              benefit card. */}
          <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {ORG_BENEFITS.map(({ icon: Icon, title, desc }) => (
              <div
                key={title}
                className="rounded-2xl bg-white ring-1 ring-black/5 shadow-sm p-6 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md"
              >
                <span className="inline-flex w-10 h-10 rounded-full bg-blue-50 ring-1 ring-blue-100 items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-blue-600" strokeWidth={2} />
                </span>
                <h4 className="font-bold text-[#0b1f3a] text-base mb-2">{title}</h4>
                <p className="text-sm text-[#0b1f3a]/65 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>

          <div
            className="sm:hidden -mx-6 px-6 flex gap-4 overflow-x-auto snap-x snap-mandatory pb-3"
            style={{ scrollbarWidth: 'none' }}
            data-testid="partners-org-benefits-mobile"
          >
            {ORG_BENEFITS.map(({ icon: Icon, title, desc }) => (
              <div
                key={title}
                className="shrink-0 w-[80vw] max-w-[320px] snap-start rounded-2xl bg-white ring-1 ring-black/5 shadow-sm p-6"
              >
                <span className="inline-flex w-10 h-10 rounded-full bg-blue-50 ring-1 ring-blue-100 items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-blue-600" strokeWidth={2} />
                </span>
                <h4 className="font-bold text-[#0b1f3a] text-base mb-2">{title}</h4>
                <p className="text-sm text-[#0b1f3a]/65 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
          <style>{`[data-testid="partners-org-benefits-mobile"]::-webkit-scrollbar{display:none;}`}</style>
          <p className="sm:hidden mt-3 text-center text-xs text-[#0b1f3a]/45 tracking-wider uppercase">
            Swipe → to explore
          </p>
        </div>
      </section>

      {/* ANALYTICS — dark navy band */}
      <section className="px-6 py-24 md:py-28 bg-[#0b1f3a] text-white">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 justify-center mb-5">
            <PieChart className="w-4 h-4 text-blue-300" />
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-300">
              Real data on training performance
            </p>
          </div>
          <h2 className="text-3xl md:text-5xl font-black leading-[1.08] tracking-tight text-center mb-10">
            Something most companies{' '}
            <em className="italic font-light text-blue-300">don&rsquo;t have.</em>
          </h2>
          <div className="space-y-6 text-lg md:text-xl text-white/80 leading-relaxed mb-12">
            <p>
              Most network marketing companies have detailed statistics on product
              sales, but{' '}
              <strong className="text-white">
                almost no data on their training and development programs
              </strong>
              . They know how much product moves, but they don&rsquo;t know if their
              training actually works.
            </p>
            <p>
              Levanta changes this. Because everything happens inside a structured
              system, we track every step of the learning and development process.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-3 mb-10">
            {ANALYTICS_POINTS.map(({ icon: Icon, text }) => (
              <div
                key={text}
                className="flex items-center gap-3 rounded-2xl bg-white/5 ring-1 ring-white/10 p-5"
              >
                <Icon className="w-5 h-5 text-blue-300 flex-shrink-0" />
                <span className="text-white/85 text-sm md:text-base">{text}</span>
              </div>
            ))}
          </div>

          <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-6 md:p-7">
            <p className="text-white/85 text-base md:text-lg leading-relaxed">
              <strong className="text-white">This means:</strong> if a training step
              shows poor results or low engagement, we can identify it and improve
              it. Instead of guessing what works, you get real data on what your
              distributors are actually learning and doing.
            </p>
          </div>
        </div>
      </section>

      {/* WORKS WITH YOUR SYSTEM */}
      <section className="px-6 py-24 md:py-28 bg-white">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-600 mb-5">
            Works with your system
          </p>
          <h2 className="text-3xl md:text-5xl font-black leading-[1.08] tracking-tight text-[#0b1f3a] mb-8">
            Levanta works alongside your structure,{' '}
            <em className="italic font-light text-blue-600">not against it.</em>
          </h2>
          <p className="text-lg md:text-xl text-[#0b1f3a]/80 leading-relaxed">
            Levanta does not replace your company&rsquo;s existing system, training,
            or structure. It{' '}
            <strong className="text-[#0b1f3a]">organizes, strengthens, and supports</strong>{' '}
            what you already have. Levanta works alongside your company structure
            and helps your people move more effectively through your existing
            process.
          </p>
        </div>
      </section>

      {/* WHY DIFFERENT */}
      <section className="px-6 py-24 md:py-28 bg-gradient-to-b from-white to-[#eaf3fb]">
        <div className="max-w-4xl mx-auto">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-600 mb-5 text-center">
            Why Levanta is different
          </p>
          <h2 className="text-3xl md:text-5xl font-black leading-[1.08] tracking-tight text-[#0b1f3a] text-center mb-10">
            Built around action,{' '}
            <em className="italic font-light text-blue-600">not information.</em>
          </h2>
          <p className="text-lg md:text-xl text-[#0b1f3a]/80 text-center max-w-2xl mx-auto leading-relaxed mb-12">
            Most systems in this space are content-heavy, motivational, or
            unstructured. They provide information but do not guide action.
            Levanta is built around:
          </p>

          <div className="grid sm:grid-cols-2 gap-4">
            {DIFFERENTIATORS.map((item) => (
              <div
                key={item}
                className="flex items-center gap-4 rounded-2xl bg-white ring-1 ring-black/5 shadow-sm p-5"
              >
                <span className="w-10 h-10 rounded-full bg-blue-50 ring-1 ring-blue-100 flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-5 h-5 text-blue-600" strokeWidth={2.2} />
                </span>
                <span className="text-[#0b1f3a] font-semibold">{item}</span>
              </div>
            ))}
          </div>

          <p className="text-center mt-10 text-2xl md:text-3xl font-bold text-[#0b1f3a]">
            We turn learning into{' '}
            <em className="italic font-light text-blue-600">action.</em>
          </p>
        </div>
      </section>

      {/* PARTNERSHIP MODEL — financial */}
      <section className="px-6 py-24 md:py-28 bg-[#fafbfd]">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 justify-center mb-5">
            <DollarSign className="w-4 h-4 text-blue-600" />
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-600">
              The partnership model
            </p>
          </div>
          <h2 className="text-3xl md:text-5xl font-black leading-[1.08] tracking-tight text-[#0b1f3a] text-center mb-10">
            Levanta pays your company,{' '}
            <em className="italic font-light text-blue-600">not the other way around.</em>
          </h2>

          <div className="space-y-7 text-lg md:text-xl text-[#0b1f3a]/80 leading-relaxed mb-10">
            <p>
              A common assumption is that a company would need to pay for this kind
              of platform and support. That is not how this partnership works.
              Instead of your company paying Levanta, Levanta pays your company
              through an affiliate revenue-share model.
            </p>
          </div>

          <div className="rounded-3xl bg-white ring-1 ring-black/5 shadow-sm p-7 md:p-9 mb-10">
            <ul className="space-y-4">
              {[
                'Levanta is not an extra cost for your company',
                'Levanta is not a software expense you need to carry',
                'Levanta becomes an additional revenue opportunity for your company',
              ].map((line) => (
                <li key={line} className="flex items-start gap-3 text-[#0b1f3a]">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" strokeWidth={2.2} />
                  <span className="text-base md:text-lg">{line}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-3xl bg-[#0b1f3a] text-white p-8 md:p-10">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-300 mb-6">
              How it works
            </p>
            <ol className="space-y-5">
              {[
                'Your company introduces distributors to Levanta',
                'Distributors use the platform and access both personal growth and MLM partner development',
                'Based on real platform usage and subscription activity, your company receives a revenue share',
              ].map((step, i) => (
                <li key={step} className="flex items-start gap-4">
                  <span className="font-mono text-xs text-blue-300 mt-1.5 flex-shrink-0">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="text-base md:text-lg text-white/90 leading-relaxed">{step}</span>
                </li>
              ))}
            </ol>
            <p className="mt-8 pt-6 border-t border-white/10 text-white/65 italic text-sm md:text-base leading-relaxed">
              A straightforward win-win: distributors get value, your company gets
              stronger distributor development, and your company also receives
              revenue share based on actual usage.
            </p>
          </div>
        </div>
      </section>

      {/* PARTNER BENEFITS SUMMARY */}
      <section className="px-6 py-24 md:py-28 bg-white">
        <div className="max-w-4xl mx-auto">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-600 mb-5 text-center">
            Partner benefits summary
          </p>
          <h2 className="text-3xl md:text-5xl font-black leading-[1.08] tracking-tight text-[#0b1f3a] text-center mb-12">
            Six reasons to{' '}
            <em className="italic font-light text-blue-600">talk to us.</em>
          </h2>

          <div className="grid sm:grid-cols-2 gap-4">
            {SUMMARY_BENEFITS.map((b) => (
              <div
                key={b}
                className="flex items-center gap-4 rounded-2xl bg-[#fafbfd] ring-1 ring-black/5 p-5"
              >
                <span className="w-10 h-10 rounded-full bg-blue-50 ring-1 ring-blue-100 flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-5 h-5 text-blue-600" strokeWidth={2.2} />
                </span>
                <span className="text-[#0b1f3a] font-semibold">{b}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA — final */}
      <section className="px-6 py-24 md:py-28 bg-[#0b1f3a]">
        <div className="max-w-3xl mx-auto text-center text-white">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-300 mb-4">
            Interested in partnership?
          </p>
          <h2 className="text-4xl md:text-5xl font-black leading-tight tracking-tight">
            Let&rsquo;s have a{' '}
            <em className="italic font-light text-blue-300">conversation.</em>
          </h2>
          <p className="mt-6 text-lg md:text-xl text-white/75 max-w-2xl mx-auto leading-relaxed">
            If you lead a network marketing company and want to explore how Levanta
            can support your organization, we&rsquo;d be glad to talk about how the
            partnership could work for your specific situation.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <button
              onClick={() => setIsModalOpen(true)}
              data-testid="partners-cta-contact"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-white text-[#0b1f3a] font-semibold shadow-md hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-200"
            >
              Contact us about partnership
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          <p className="mt-6 text-xs text-white/40">
            No commitment required. Just a conversation.
          </p>
        </div>
      </section>

      {/* PARTNERSHIP CONTACT MODAL */}
      {isModalOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          onClick={closeModal}
          data-testid="partners-modal"
        >
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div
            className="relative w-full max-w-lg bg-white rounded-3xl overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 z-10 w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
              data-testid="partners-modal-close"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>

            <div className="p-8">
              {success ? (
                <div className="text-center py-8">
                  <div className="w-20 h-20 bg-blue-50 ring-1 ring-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-10 h-10 text-blue-600" />
                  </div>
                  <h2 className="text-2xl font-black text-[#0b1f3a] mb-3">Thank you.</h2>
                  <p className="text-[#0b1f3a]/65 mb-6">
                    We received your information and will be in touch soon to
                    discuss the partnership opportunity.
                  </p>
                  <Button
                    onClick={closeModal}
                    className="bg-[#0b1f3a] hover:bg-[#0b1f3a]/90 text-white rounded-full px-6"
                  >
                    Close
                  </Button>
                </div>
              ) : (
                <>
                  <div className="text-center mb-6">
                    <div className="w-14 h-14 bg-blue-50 ring-1 ring-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Handshake className="w-7 h-7 text-blue-600" />
                    </div>
                    <h2 className="text-2xl font-black text-[#0b1f3a] mb-2">
                      Partnership inquiry
                    </h2>
                    <p className="text-[#0b1f3a]/65 text-sm">
                      Fill in your details and we&rsquo;ll reach out to discuss
                      partnership opportunities.
                    </p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4" data-testid="partners-form">
                    <div>
                      <label className="block text-sm font-semibold text-[#0b1f3a] mb-1">
                        Full name *
                      </label>
                      <Input
                        type="text" name="full_name"
                        value={formData.full_name} onChange={handleChange}
                        placeholder="Your full name" required className="w-full"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-[#0b1f3a] mb-1">
                        Role in the company *
                      </label>
                      <Input
                        type="text" name="role"
                        value={formData.role} onChange={handleChange}
                        placeholder="e.g. CEO, Founder, VP of Sales"
                        required className="w-full"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-[#0b1f3a] mb-1">
                        Company name *
                      </label>
                      <Input
                        type="text" name="company_name"
                        value={formData.company_name} onChange={handleChange}
                        placeholder="Your company name"
                        required className="w-full"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-[#0b1f3a] mb-1">
                        Email *
                      </label>
                      <Input
                        type="email" name="email"
                        value={formData.email} onChange={handleChange}
                        placeholder="your@email.com"
                        required className="w-full"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-[#0b1f3a] mb-1">
                        Phone number *
                      </label>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <div className="relative">
                          <select
                            name="countryCode"
                            value={formData.countryCode}
                            onChange={handleChange}
                            className="appearance-none bg-white border border-gray-200 rounded-lg px-3 py-2 pr-8 text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent h-10 w-full sm:min-w-[180px]"
                          >
                            {countryCodes.map((item, index) => (
                              <option key={`${item.code}-${index}`} value={item.code}>
                                {item.code} {item.country}
                              </option>
                            ))}
                          </select>
                          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                        </div>
                        <Input
                          type="tel" name="phoneNumber"
                          value={formData.phoneNumber} onChange={handleChange}
                          placeholder="123 456 7890"
                          required className="flex-1"
                        />
                      </div>
                    </div>

                    {error && (
                      <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm">
                        {error}
                      </div>
                    )}

                    <Button
                      type="submit"
                      disabled={loading}
                      data-testid="partners-form-submit"
                      className="w-full bg-[#0b1f3a] hover:bg-[#0b1f3a]/90 text-white py-6 text-base rounded-full font-semibold"
                    >
                      {loading ? 'Submitting...' : 'Submit inquiry'}
                    </Button>

                    <div className="flex items-start gap-3 bg-[#fafbfd] rounded-xl p-4 mt-4 ring-1 ring-black/5">
                      <Lock className="w-5 h-5 text-[#0b1f3a]/40 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-[#0b1f3a]/55 leading-relaxed">
                        Your information is private and confidential. It will not
                        be used for marketing purposes or shared with anyone. This
                        information is solely for the purpose of discussing the
                        partnership opportunity with you.
                      </p>
                    </div>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <AltFooter />
    </div>
  );
};

export default PartnersPage;
